import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState, useEffect } from 'react';
import { getAccessCodeFeatureConfig, checkAccessCodeFeatureEnabled } from '../../graphql/queries/accessCodeQueries';

const GET_CURRENT_DRIVER_DSP_ID = gql`
    query GetCurrentDriverDspId {
        getCurrentDriverDspId
    }
`;

/**
 * 检查当前司机是否启用门禁码功能（前端灰度判断）
 * 用于前端判断是否显示门禁码功能入口
 * 
 * 后端提供司机的 DSP ID，前端做灰度判断
 * 
 * @returns {Object} 包含 isEnabled 和 loading 状态
 * @example
 * ```tsx
 * function DeliveryPage() {
 *   const { isEnabled, loading } = useAccessCodeFeature();
 *   
 *   if (loading) return <Loading />;
 *   
 *   return (
 *     <div>
 *       {isEnabled && <AccessCodeButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAccessCodeFeature() {
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // 获取司机的 DSP ID
    const { data: dspData, loading: dspLoading, error: dspError } = useQuery<{ getCurrentDriverDspId: number | null }>(
        GET_CURRENT_DRIVER_DSP_ID,
        {
            fetchPolicy: 'cache-first', // 使用缓存优先策略
        }
    );

    useEffect(() => {
        const checkFeature = async () => {
            try {
                setLoading(true);
                
                // 等待 DSP 数据加载完成
                if (dspLoading) return;
                
                if (dspError) {
                    setError(dspError);
                    setIsEnabled(false);
                    return;
                }
                
                // 获取配置值
                const configValue = await getAccessCodeFeatureConfig();
                
                // 前端灰度判断
                const dspId = dspData?.getCurrentDriverDspId;
                const enabled = checkAccessCodeFeatureEnabled(dspId, configValue);
                setIsEnabled(enabled);
                
                console.log('门禁码功能灰度判断:', { dspId, configValue, enabled });
            } catch (err) {
                console.error('Failed to check access code feature enabled:', err);
                setError(err as Error);
                setIsEnabled(false);
            } finally {
                setLoading(false);
            }
        };

        checkFeature();
    }, [dspData, dspLoading, dspError]);

    return {
        isEnabled,
        loading: loading || dspLoading,
        error: error || dspError
    };
}
